import dayjs from "dayjs"

import {
  type AuditActor,
  type AuditLogEntry
} from "../../../../types/audit-log.types.ts"
import { normalizeBackendChanges } from "../../../../utils/audit-log/normalize-backend-changes.ts"
import type { Lead, LeadEvent } from "../../types/lead.types.ts"
import { toWallClock } from "./lead-activity-date.ts"
import type {
  LeadActivityCategory,
  LeadActivityEntry,
  LeadActivityFilters,
  LeadActivityUser
} from "./lead-activity.types.ts"

const LEAD_ACTIVITY_CATEGORIES: LeadActivityCategory[] = [
  "appointments",
  "followUps",
  "notes",
  "stageChanges",
  "system"
]

/**
 * Audit rows key their diff by the backend's own field and calendar-action
 * names; anything unlisted is a plain record change and reads as System.
 */
const CATEGORY_BY_CHANGE_KEY: Record<string, LeadActivityCategory> = {
  status: "stageChanges",
  internal_notes: "notes",
  comments: "notes",
  lost_notes: "notes",
  follow_up_date: "followUps",
  follow_up_time: "followUps",
  follow_up_owner: "followUps",
  reminder: "followUps",
  event: "appointments",
  events: "appointments",
  appointment: "appointments",
  appointment_date: "appointments",
  appointment_time: "appointments",
  participants: "appointments",
  rsvp: "appointments",
  visit: "appointments",
  checklist: "appointments",
  attachment: "appointments"
}

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

interface ResolvedUser {
  userId: string | null
  userLabel: string | null
}

const SYSTEM_USER: ResolvedUser = { userId: null, userLabel: null }

/** A raw id is never shown as a name: an unresolvable actor reads as the system. */
const resolveUser = (
  raw: unknown,
  userLabels: Record<string, string>
): ResolvedUser => {
  if (raw == null) return SYSTEM_USER

  if (typeof raw === "object" && !Array.isArray(raw)) {
    const actor = raw as AuditActor
    const id = actor.id != null ? String(actor.id) : null
    const label =
      actor.full_name?.trim() ||
      actor.email?.trim() ||
      (id ? userLabels[id] : "") ||
      ""

    return label ? { userId: id, userLabel: label } : SYSTEM_USER
  }

  const value = String(raw).trim()
  if (!value) return SYSTEM_USER

  const known = userLabels[value]
  if (known) return { userId: value, userLabel: known }
  if (UUID_LIKE.test(value)) return SYSTEM_USER

  return { userId: value, userLabel: value }
}

/** The lead carries its creator as an id and a name in two separate fields. */
const resolveNamedUser = (
  id: unknown,
  name: unknown,
  userLabels: Record<string, string>
): ResolvedUser => {
  const byId = resolveUser(id, userLabels)
  if (byId.userLabel) return byId

  const label = name == null ? "" : String(name).trim()
  if (!label) return SYSTEM_USER

  return { userId: id ? String(id) : label, userLabel: label }
}

const humanizeKey = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

const asFieldMap = (
  changes: unknown
): Record<string, { from: unknown; to: unknown }> | null => {
  const normalized = normalizeBackendChanges(changes)
  if (
    !normalized ||
    typeof normalized !== "object" ||
    Array.isArray(normalized)
  )
    return null

  const entries = Object.entries(normalized as Record<string, unknown>)
  if (!entries.length) return null

  const isFromTo = entries.every(
    ([, value]) =>
      value != null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "from" in (value as object) &&
      "to" in (value as object)
  )

  return isFromTo
    ? (normalized as Record<string, { from: unknown; to: unknown }>)
    : null
}

const categoryForKeys = (keys: string[]): LeadActivityCategory => {
  const mapped = keys
    .map((key) => CATEGORY_BY_CHANGE_KEY[key])
    .filter(Boolean) as LeadActivityCategory[]

  if (!mapped.length) return "system"
  if (mapped.includes("stageChanges")) return "stageChanges"

  const [first] = mapped
  return mapped.every((category) => category === first) ? first : "system"
}

const displayValue = (value: unknown): string => {
  if (value == null || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

/** An appointment that loses its value was removed, not blanked. */
const APPOINTMENT_CHANGE_KEYS = new Set(["event", "events"])

const isRemoval = (key: string, to: unknown) =>
  APPOINTMENT_CHANGE_KEYS.has(key) && (to == null || to === "")

/** Every key in a row matters: one calendar action can carry several. */
const describeChanges = (
  fields: Record<string, { from: unknown; to: unknown }>,
  category: LeadActivityCategory
): string =>
  Object.entries(fields)
    .map(([key, { from, to }]) => {
      const label = humanizeKey(key)
      const arrow = `${displayValue(from)} → ${displayValue(to)}`

      if (category === "stageChanges" && key === "status") return arrow
      if (isRemoval(key, to)) return `${label}: deleted`
      if (from == null || from === "") return `${label}: ${displayValue(to)}`
      return `${label}: ${arrow}`
    })
    .join(" · ")

const CREATE_ACTIONS = new Set(["create", "created"])

const isCreateAction = (action: unknown): boolean =>
  CREATE_ACTIONS.has(String(action ?? "").trim().toLowerCase())

const LEAD_TARGET_PREFIXES = ["lead ", "project "]

const targetLabel = (entry: AuditLogEntry): string =>
  String(entry?.target ?? "").trim()

/**
 * Only the lead's own creation duplicates the `created` timestamp, so only that
 * row is dropped; a milestone's Create row is its own fact and keeps its card.
 * The object is identified by `target_id`, falling back to the `"{Type} {name}"`
 * label for rows written before it, and to the lead for rows carrying neither.
 */
const isLeadOwnRow = (entry: AuditLogEntry, leadId: unknown): boolean => {
  const targetId = entry?.target_id == null ? "" : String(entry.target_id).trim()
  if (targetId) return targetId === String(leadId ?? "").trim()

  const label = targetLabel(entry).toLowerCase()
  if (!label) return true

  return LEAD_TARGET_PREFIXES.some((prefix) => label.startsWith(prefix))
}

const isReminder = (event: LeadEvent) =>
  String(event?.type ?? "").toLowerCase() === "reminder"

interface ComposeLeadActivityInput {
  lead: Lead
  auditEntries?: AuditLogEntry[] | null
  userLabels?: Record<string, string>
  userFormat?: string | null
  timezone?: string | null
}

/** Newest first — a lead's history is read from what just happened backwards. */
const composeLeadActivity = ({
  lead,
  auditEntries,
  userLabels = {},
  userFormat,
  timezone
}: ComposeLeadActivityInput): LeadActivityEntry[] => {
  const entries: LeadActivityEntry[] = []
  const moment = (value: unknown) => toWallClock(value, userFormat, timezone)

  let created = false

  const createdAt = moment(lead?.created)
  if (createdAt) {
    created = true
    entries.push({
      id: `lead-created-${lead.id}`,
      category: "system",
      at: createdAt,
      titleKey: "leadWorkspace.activity.entry.leadCreated",
      description: lead.lead_name ?? null,
      ...resolveNamedUser(lead.created_by_id, lead.created_by_name, userLabels)
    })
  }

  ;(auditEntries ?? []).forEach((entry) => {
    const at = moment(entry?.timestamp)
    if (!at) return

    const creation = isCreateAction(entry.action)

    if (creation && isLeadOwnRow(entry, lead?.id)) {
      if (created) return

      created = true
      entries.push({
        id: `audit-${entry.id}`,
        category: "system",
        at,
        titleKey: "leadWorkspace.activity.entry.leadCreated",
        description: lead?.lead_name ?? null,
        ...resolveUser(entry.actor ?? entry.actor_name, userLabels)
      })
      return
    }

    const fields = asFieldMap(entry.changes)
    const category = fields ? categoryForKeys(Object.keys(fields)) : "system"
    const changed = fields
      ? describeChanges(fields, category)
      : (entry.action ?? null)

    entries.push({
      id: `audit-${entry.id}`,
      category,
      at,
      titleKey: creation
        ? "leadWorkspace.activity.entry.created"
        : `leadWorkspace.activity.entry.${category}`,
      description: creation ? targetLabel(entry) || changed : changed,
      ...resolveUser(entry.actor ?? entry.actor_name, userLabels)
    })
  })
  ;(lead?.lead_calendar_events ?? []).forEach((event) => {
    const at = moment(event?.start_at)
    if (!at) return

    entries.push({
      id: `event-${event.id}`,
      category: isReminder(event) ? "followUps" : "appointments",
      at,
      titleKey: isReminder(event)
        ? "leadWorkspace.activity.entry.reminder"
        : "leadWorkspace.activity.entry.appointment",
      description: event.title ?? null,
      ...resolveUser(event.created_by, userLabels)
    })
  })

  return entries.sort((a, b) => dayjs(b.at).valueOf() - dayjs(a.at).valueOf())
}

const NO_FILTERS: LeadActivityFilters = {
  category: "all",
  after: null,
  before: null,
  user: null
}

const filterLeadActivity = (
  entries: LeadActivityEntry[],
  filters: LeadActivityFilters = NO_FILTERS
): LeadActivityEntry[] =>
  entries.filter((entry) => {
    if (filters.category !== "all" && entry.category !== filters.category)
      return false

    const at = dayjs(entry.at)
    if (filters.after && at.isBefore(dayjs(filters.after), "day")) return false
    if (filters.before && at.isAfter(dayjs(filters.before), "day")) return false
    if (filters.user && entry.userId !== filters.user) return false

    return true
  })

/** The user filter offers only the people the composed history actually names. */
const leadActivityUsers = (
  entries: LeadActivityEntry[]
): LeadActivityUser[] => {
  const byId = new Map<string, string>()

  entries.forEach(({ userId, userLabel }) => {
    if (userId && userLabel && !byId.has(userId)) byId.set(userId, userLabel)
  })

  return [...byId.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export {
  LEAD_ACTIVITY_CATEGORIES,
  NO_FILTERS,
  composeLeadActivity,
  filterLeadActivity,
  leadActivityUsers
}
